<script setup lang="ts">
import { ref } from 'vue'

/**
 * La galería de un producto.
 *
 * Se desliza de lado, y eso funciona — pero nada decía que hubiera más fotos.
 * Una imagen sola parece una imagen sola: el cliente veía la primera y se
 * quedaba con esa. El contador y los puntos son el idioma que todo el mundo ya
 * conoce del carrete del teléfono; no hay que explicarlos.
 */
const props = defineProps<{ fotos: string[]; nombre: string }>()

const tira = ref<HTMLElement | null>(null)
const actual = ref(0)

/**
 * El paso real entre fotos.
 *
 * No es el ancho del contenedor: cada foto ocupa el 91 % para que la siguiente
 * se asome. Medirlo del propio nodo evita que el contador se desfase a la
 * tercera o cuarta imagen.
 */
function paso(el: HTMLElement): number {
  const primera = el.firstElementChild as HTMLElement | null
  if (!primera) return el.clientWidth || 1
  const estilo = getComputedStyle(el)
  const hueco = parseFloat(estilo.columnGap || estilo.gap || '0') || 0
  return primera.offsetWidth + hueco
}

function alDeslizar(): void {
  const el = tira.value
  if (!el) return
  actual.value = Math.round(el.scrollLeft / paso(el))
}

/** Los puntos también sirven de botón: en escritorio no hay dedo que deslice. */
function ir(i: number): void {
  const el = tira.value
  if (!el) return
  const destino = Math.max(0, Math.min(i, props.fotos.length - 1))
  el.scrollTo({ left: destino * paso(el), behavior: 'smooth' })
}

/** Las flechas: un punto de 7 px no es un objetivo razonable para un cursor. */
function mover(delta: number): void {
  ir(actual.value + delta)
}
</script>

<template>
  <div class="gal">
    <div ref="tira" class="gal-tira" @scroll.passive="alDeslizar">
      <img
        v-for="(f, i) in fotos" :key="f"
        :src="f" :alt="`${nombre} — foto ${i + 1}`"
        class="gal-img" loading="lazy"
      />
    </div>

    <template v-if="fotos.length > 1">
      <button
        class="gal-flecha izq" type="button" aria-label="Foto anterior"
        :disabled="actual === 0" @click="mover(-1)"
      >‹</button>
      <button
        class="gal-flecha der" type="button" aria-label="Foto siguiente"
        :disabled="actual >= fotos.length - 1" @click="mover(1)"
      >›</button>
      <span class="gal-cuenta">{{ actual + 1 }} / {{ fotos.length }}</span>
      <div class="gal-puntos">
        <button
          v-for="(f, i) in fotos" :key="f"
          class="gal-punto" :class="{ activo: i === actual }"
          type="button" :aria-label="`Ver foto ${i + 1}`" @click="ir(i)"
        ></button>
      </div>
    </template>
  </div>
</template>
